import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { internApi } from '../../services/internApi';
import { activityApi } from '../../services/activityApi';

// ===== Rotation checklist (Observed / Performed / Remark) =====
// Aligned with Start Activity (Quick Start) structure.
// User-facing protected terminology used by larger platforms is intentionally avoided.
// Safer product terminology: Core Practice Area → Competency → Skill.
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const CPA_OPD = `${CORE_PRACTICE_AREA_LABEL} — OPD consultation (assessment → plan → referral)`;
const CPA_MNCH = `${CORE_PRACTICE_AREA_LABEL} — MNCH day (ANC/PNC/Newborn/Under-5)`;
const CPA_IMMUNIZATION = `${CORE_PRACTICE_AREA_LABEL} — Immunization session (service delivery + safety)`;
const CPA_NCD = `${CORE_PRACTICE_AREA_LABEL} — NCD clinic & preventive oncology`;
const CPA_PROGRAMME = `${CORE_PRACTICE_AREA_LABEL} — Programme & outreach activities`;

const ACT_OPD_COMMON = 'Diagnose & Manage Common Ailments';
const ACT_OPD_RED_FLAGS = 'Emergency triage, red flags & referral';

const ACT_NVD = 'Conduct Normal vaginal delivery';
const ACT_ANC = 'Provide antenatal care';
const ACT_PNC = 'Provide post-natal care';
const ACT_IMNCI = 'IMNCI: management of childhood illnesses';
const ACT_GROWTH = 'Growth monitoring by anthropometry';
const ACT_BF = 'Counselling – Breastfeeding';
const ACT_IYCF = 'Counselling – IYCF';
const ACT_KMC = 'Counselling – Kangaroo Mother Care';

const ACT_IMM_SESSION = 'Immunization session / service delivery';
const ACT_BCG = 'Perform vaccination: BCG';
const ACT_PENTA = 'Perform vaccination: Pentavalent';
const ACT_MEASLES = 'Perform vaccination: Measles';
const ACT_NEWER_VACCINES = 'Perform vaccination: Other newer vaccines';

const ACT_IPC = 'Infection Prevention & Control (IPC)';
const ACT_TB_NTEP = 'TB Screening/Notification & Contact Management (NTEP)';
const ACT_VHND = 'Village Health, Sanitation & Nutrition Day (VHSND/VHND)';

const FORTNIGHTLY_SECTION_NOTES = {
  [CPA_OPD]:
    'Common investigations and ENT/Ophthal complaints are counted under Diagnose & Manage Common Ailments, not as separate top-level rows.',
};

const FORTNIGHTLY_SECTIONS = [
  {
    title: CPA_OPD,
    items: [
      ACT_OPD_COMMON,
      ACT_OPD_RED_FLAGS,
    ],
  },
  {
    title: CPA_MNCH,
    items: [
      ACT_NVD,
      ACT_ANC,
      ACT_PNC,
      ACT_IMNCI,
      ACT_GROWTH,
      ACT_BF,
      ACT_IYCF,
      ACT_KMC,
    ],
  },
  {
    title: CPA_IMMUNIZATION,
    items: [
      ACT_IMM_SESSION,
      ACT_BCG,
      ACT_PENTA,
      ACT_MEASLES,
      ACT_NEWER_VACCINES,
      'Monitoring of cold chain',
      'Calculate wastage factor of vaccines',
      'Recording and reporting of AEFI',
      'Give 4 key messages of immunization',
      'Yellow fever vaccination & documentation',
      'Counselling – Optional & travel vaccines',
    ],
  },
  {
    title: CPA_NCD,
    items: [
      'Assessment of hypertension',
      'Assessment of hyperglycaemia',
      'Assessment of dyslipidemia',
      'Assessment of COPD',
      'Assessment of hypothyroidism',
      'Non-pharmacological measures for NCD',
      'Screening for depression (standard scale)',
      'Screening for dependence (standard scale)',
      'Counselling for preventable cancers / early diagnosis',
      'Screening – Breast examination',
      'Screening – Oral examination',
      'Screening – Cervical (VIA)',
    ],
  },
  {
    title: CPA_PROGRAMME,
    items: [
      ACT_IPC,
      ACT_TB_NTEP,
      ACT_VHND,
    ],
  },
  {
    title: 'Competency — Counselling & patient communication',
    items: [
      'Counselling – Family planning',
      'Counselling – Nutritional interventions',
      'Counselling – Diarrhoea',
      'Counselling – ARI',
      // Keep generic counselling as a competency “marker”
      'Counselling',
    ],
  },
  {
    title: 'Skill — Vaccine delivery (hands-on)',
    items: [
      ACT_BCG,
      ACT_PENTA,
      ACT_MEASLES,
      ACT_NEWER_VACCINES,
    ],
  },
];

const ACTIVITY_COUNT_RULES = [
  {
    section: CPA_MNCH,
    item: ACT_NVD,
    metric: 'performed',
    keys: ['nvd', 'normal delivery', 'normal vaginal delivery', 'conduct normal delivery'],
  },
  {
    section: CPA_MNCH,
    item: ACT_IMNCI,
    metric: 'performed',
    keys: ['imnci-under5', 'under-5 sick child', 'under 5 sick child', 'imnci', 'imci'],
  },
  {
    section: CPA_IMMUNIZATION,
    item: ACT_IMM_SESSION,
    metric: 'performed',
    keys: ['uip-immunization', 'immunization session', 'immunisation session', 'universal immunization', 'universal immunisation', 'uip'],
  },
  {
    section: CPA_OPD,
    item: ACT_OPD_COMMON,
    metric: 'performed',
    keys: ['opd-primary-care', 'opd primary care', 'primary care consult', 'diagnose manage common ailments', 'common ailments', 'ent ophthalmology', 'ophthal', 'diagnostic tests'],
  },
  {
    section: CPA_PROGRAMME,
    item: ACT_IPC,
    metric: 'performed',
    keys: ['ipc-safety', 'infection prevention', 'infection control', 'ipc universal', 'ipc'],
  },
  {
    section: CPA_PROGRAMME,
    item: ACT_TB_NTEP,
    metric: 'performed',
    keys: ['ntep-tb-workflow', 'tb screening', 'tb notification', 'contact management', 'ntep'],
  },
  {
    section: CPA_PROGRAMME,
    item: ACT_VHND,
    metric: 'performed',
    keys: ['vhnd-session', 'vhsnd', 'vhnd', 'village health sanitation nutrition day', 'village health'],
  },
];

// ✅ keep both spellings to avoid mismatch/no regression
const CENTRES = ['CRHA Keru', 'CRHA Dhawa', 'CRHA Dundhara', 'CRHA Dhundhara', 'UHTC Pratap Nagar'];

// freeform backfill row
const emptyItem = () => ({
  date: '',
  time: '',
  description: '',
  location: '',
  patientsSeen: 0,
  notes: '',
});
const wk = () => ({ observed: 0, performed: 0, remark: '' });

// ---------- tenant/auth helpers ----------
function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
}

function isObjectId(v) {
  return typeof v === 'string' && /^[a-f\d]{24}$/i.test(v.trim());
}

function getTenantId() {
  try {
    const candidates = [];

    const t1 = localStorage.getItem('tenantId');
    const t2 = localStorage.getItem('activeTenantId');
    const t3 = sessionStorage.getItem('tenantId');
    const t4 = sessionStorage.getItem('activeTenantId');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const safeParse = (v) => {
      try {
        return JSON.parse(v);
      } catch {
        return null;
      }
    };

    const lu = safeParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeParse(sessionStorage.getItem('loggedInUser') || 'null');

    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantId,
        obj?.tenantId,
        core?.tenant?._id,
        core?.tenant?.id,
        obj?.tenant?._id,
        obj?.tenant?.id,
      );
    });

    const hit = candidates
      .map((x) => (x == null ? '' : String(x)).trim())
      .find((x) => isObjectId(x));

    return hit || '';
  } catch {
    return '';
  }
}

// ✅ robust token lookup (NO regression)
function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const k = store.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}
function getAuthToken() {
  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferred = ['cmfm_token', 'tenant_token', 'portal_token', 'jwt', 'token', 'authToken'];
      for (const k of preferred) {
        const hit = keys.find((x) => String(x).toLowerCase() === String(k).toLowerCase());
        if (hit) return store.getItem(hit);
      }
      const nonOwnerKey = keys.find((k) => /token/i.test(k) && !/owner/i.test(k));
      if (nonOwnerKey) return store.getItem(nonOwnerKey);
      const any = keys.find((k) => /token/i.test(k));
      return any ? store.getItem(any) : null;
    } catch {
      return null;
    }
  };
  return pick(localStorage) || pick(sessionStorage);
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const p = String(path || '');
  if (!slug) return p;
  if (!p.startsWith('/')) return `/t/${slug}/${p}`.replace(/\/{2,}/g, '/');
  if (p.startsWith(`/t/${slug}`)) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

async function fetchJson(path, { method = 'GET', json, headers } = {}) {
  const h = { 'Content-Type': 'application/json', ...(headers || {}) };
  const t = getAuthToken();
  if (t) h.Authorization = `Bearer ${t}`;

  const slug = getTenantSlug();
  if (slug) h['X-Tenant-Slug'] = slug;

  // ✅ NO REGRESSION: only set tenant-id if it's a real ObjectId, never the slug
  const tenantId = getTenantId();
  if (tenantId) h['X-Tenant-Id'] = tenantId;

  const url = tenantUrl(path);
  const r = await fetch(url, {
    method,
    headers: h,
    body: json ? JSON.stringify(json) : undefined,
    credentials: 'include',
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const msg = data?.error || data?.message || `Request failed: ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

// simple JSON getter (same idea as Layout)
const jget = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k) || 'null');
  } catch {
    return null;
  }
};

// ---------- small utils ----------
const getParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
};
const pickArray = (p) => {
  if (Array.isArray(p?.items)) return p.items;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.rows)) return p.rows;
  if (Array.isArray(p)) return p;
  return [];
};
// drop undefined/empty keys so qs never contains from=undefined etc.
const cleanParams = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    out[k] = v;
  });
  return out;
};

const sanitizeDisplayText = (value) => {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const legacyAbbrev = ['E', 'P', 'A'].join('');
  const legacyFullForm = [['Ent', 'rustable'].join(''), ['Pro', 'fessional'].join(''), ['Act', 'ivities'].join('')].join('\\s+');

  return input
    .replace(new RegExp(`${legacyFullForm}\\s*\\(\\s*${legacyAbbrev}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(legacyFullForm, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${legacyAbbrev}s\\b`, 'g'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${legacyAbbrev}\\b`, 'g'), CORE_PRACTICE_AREA_LABEL);
};

const sanitizeDisplayArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => sanitizeDisplayText(item)).filter(Boolean);
};

const createEmptyFortnightly = () => {
  const out = {};
  FORTNIGHTLY_SECTIONS.forEach((sec) => {
    out[sec.title] = {};
    sec.items.forEach((it) => {
      out[sec.title][it] = wk();
    });
  });
  return out;
};

const normalizeCellForMatrix = (cell = {}) => ({
  observed: Math.max(0, Number(cell?.observed || 0)),
  performed: Math.max(0, Number(cell?.performed || 0)),
  remark: sanitizeDisplayText(cell?.remark || ''),
  corePracticeAreaTitle: sanitizeDisplayText(cell?.corePracticeAreaTitle || ''),
  practiceAreaTitle: sanitizeDisplayText(cell?.practiceAreaTitle || cell?.corePracticeAreaTitle || ''),
  activityKey: String(cell?.activityKey || '').trim(),
  sourceFlow: String(cell?.sourceFlow || '').trim(),
  competencies: sanitizeDisplayArray(cell?.competencies || []),
  skills: sanitizeDisplayArray(cell?.skills || []),
});

const findFortnightlyTargetBySectionAndItem = (sectionLabel, itemLabel) => {
  const sectionKey = normalizeActivityKey(sectionLabel);
  const itemKey = normalizeActivityKey(itemLabel);
  if (!itemKey) return null;

  const section = FORTNIGHTLY_SECTIONS.find((sec) => normalizeActivityKey(sec.title) === sectionKey) || null;
  if (section) {
    const item = section.items.find((it) => normalizeActivityKey(it) === itemKey);
    if (item) return { section: section.title, item, metric: 'performed' };
  }

  for (const sec of FORTNIGHTLY_SECTIONS) {
    const item = sec.items.find((it) => normalizeActivityKey(it) === itemKey);
    if (item) return { section: sec.title, item, metric: 'performed' };
  }

  return null;
};

const findFortnightlyTargetFromChecklistCell = (label, cell = {}) => {
  const normalizedCell = normalizeCellForMatrix(cell);
  const direct = findFortnightlyTargetBySectionAndItem(
    normalizedCell.corePracticeAreaTitle || normalizedCell.practiceAreaTitle,
    label,
  );
  if (direct) return direct;

  const text = normalizeActivityKey([
    label,
    normalizedCell.activityKey,
    normalizedCell.corePracticeAreaTitle,
    normalizedCell.practiceAreaTitle,
    normalizedCell.sourceFlow,
    ...(normalizedCell.competencies || []),
    ...(normalizedCell.skills || []),
  ].filter(Boolean).join(' '));

  const pseudoDoc = {
    templateTitle: label,
    activityKey: normalizedCell.activityKey,
    corePracticeAreaTitle: normalizedCell.corePracticeAreaTitle,
    practiceAreaTitle: normalizedCell.practiceAreaTitle,
    finalGroup: normalizedCell.corePracticeAreaTitle || normalizedCell.practiceAreaTitle,
    competencies: normalizedCell.competencies,
    skills: normalizedCell.skills,
    notes: text,
  };

  return resolveFortnightlyTarget(pseudoDoc);
};

const mergeStoredFortnightlyCell = (draft, target, storedCell) => {
  if (!target?.section || !target?.item) return;
  if (!draft?.[target.section]?.[target.item]) return;

  const src = normalizeCellForMatrix(storedCell);
  const dst = { ...draft[target.section][target.item] };

  // Use max instead of addition to avoid double-counting the same approved guided
  // activity once from backend checklist sync and once from local approved-history scan.
  dst.observed = Math.max(Number(dst.observed || 0), Number(src.observed || 0));
  dst.performed = Math.max(Number(dst.performed || 0), Number(src.performed || 0));
  if (src.remark && !String(dst.remark || '').trim()) dst.remark = src.remark;

  draft[target.section][target.item] = dst;
};

const mergeStoredChecklistIntoFortnightly = (draft, checklist) => {
  const matrix = checklist?.matrix || {};
  const entries = matrix instanceof Map ? Array.from(matrix.entries()) : Object.entries(matrix || {});

  entries.forEach(([label, cell]) => {
    const safeLabel = sanitizeDisplayText(label);
    if (!safeLabel) return;
    const target = findFortnightlyTargetFromChecklistCell(safeLabel, cell);
    if (!target) return;
    mergeStoredFortnightlyCell(draft, target, cell);
  });
};

const normalizeCentreName = (value) => {
  const raw = String(value || '').trim();
  const v = raw.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!v) return '';
  if (v.includes('keru')) return 'CRHA Keru';
  if (v.includes('dhawa')) return 'CRHA Dhawa';
  if (v.includes('pratap')) return 'UHTC Pratap Nagar';
  if (v.includes('dundhara') || v.includes('dhundhara') || v.includes('dhundara') || v.includes('dundara')) {
    return 'CRHA Dhundhara';
  }
  return raw;
};

const normalizeActivityKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[_/()–—-]+/g, ' ')
  .replace(/[^a-z0-9\s]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const buildActivitySearchText = (doc) => {
  const entryText = normalizeEntries(doc)
    .map((row) => {
      const r = coerceRow(row);
      return [
        r.description,
        r.text,
        r.label,
        r.question,
        r.title,
        r.notes,
        r.valueText,
        r.answer,
        r.value,
      ].filter(Boolean).join(' ');
    })
    .join(' ');

  return normalizeActivityKey([
    doc?.templateSlug,
    doc?.slug,
    doc?.activityKey,
    doc?.sourceFlow,
    doc?.template?.slug,
    doc?.template?.templateSlug,
    doc?.template?.key,
    doc?.templateTitle,
    doc?.template?.title,
    doc?.template?.name,
    doc?.activityTitle,
    doc?.title,
    doc?.templateName,
    doc?.corePracticeAreaTitle,
    doc?.practiceAreaTitle,
    doc?.finalGroup,
    doc?.reportingTarget?.section,
    doc?.reportingTarget?.item,
    doc?.reportingTarget?.notes,
    ...(Array.isArray(doc?.competencies) ? doc.competencies : []),
    ...(Array.isArray(doc?.skills) ? doc.skills : []),
    doc?.description,
    doc?.summary,
    doc?.internNotes,
    doc?.notes,
    entryText,
  ].filter(Boolean).join(' '));
};

const isApprovedForRotationReport = (doc, forceApproved = false) => {
  if (forceApproved) return true;
  const st = String(doc?.status || '').toLowerCase();
  return st === 'approved' || st === 'observed-approved' || st.includes('approved');
};

const findDirectFortnightlyTarget = (doc) => {
  const text = buildActivitySearchText(doc);
  if (!text) return null;

  for (const sec of FORTNIGHTLY_SECTIONS) {
    const secKey = normalizeActivityKey(sec.title);
    for (const item of sec.items) {
      const itemKey = normalizeActivityKey(item);
      if (itemKey && text.includes(itemKey) && (!secKey || text.includes(secKey) || text.includes('fortnightly'))) {
        return { section: sec.title, item, metric: 'performed' };
      }
    }
  }

  return null;
};

const resolveFortnightlyTarget = (doc) => {
  const reported = doc?.reportingTarget && typeof doc.reportingTarget === 'object'
    ? findFortnightlyTargetBySectionAndItem(doc.reportingTarget.section, doc.reportingTarget.item)
    : null;
  if (reported) {
    return {
      ...reported,
      metric: doc.reportingTarget.metric === 'observed' ? 'observed' : 'performed',
    };
  }

  const direct = findDirectFortnightlyTarget(doc);
  if (direct) return direct;

  const text = buildActivitySearchText(doc);
  if (!text) return null;

  return ACTIVITY_COUNT_RULES.find((rule) =>
    (rule.keys || []).some((key) => text.includes(normalizeActivityKey(key)))
  ) || null;
};

const getFortnightlyCountDelta = (doc, target) => {
  const metric = target?.metric === 'observed' ? 'observed' : 'performed';
  const countValue = Number(doc?.counts?.[metric]);
  if (Number.isFinite(countValue) && countValue > 0) return Math.max(1, countValue);

  if (metric === 'performed') {
    const performed = Number(doc?.counts?.performed);
    if (Number.isFinite(performed) && performed > 0) return Math.max(1, performed);
  }

  if (metric === 'observed') {
    const observed = Number(doc?.counts?.observed);
    if (Number.isFinite(observed) && observed > 0) return Math.max(1, observed);
  }

  return 1;
};

const addFortnightlyCount = (draft, target, doc = {}) => {
  if (!target?.section || !target?.item) return;
  if (!draft?.[target.section]?.[target.item]) return;

  const metric = target.metric === 'observed' ? 'observed' : 'performed';
  const cell = { ...draft[target.section][target.item] };
  cell[metric] = Number(cell[metric] || 0) + getFortnightlyCountDelta(doc, target);

  const remark = sanitizeDisplayText(doc?.reportingTarget?.notes || '');
  if (remark && !String(cell.remark || '').trim()) cell.remark = remark;

  draft[target.section][target.item] = cell;
};

const sortByNewest = (a, b) => new Date(b?.date || b?.activityDate || b?.createdAt || b?.updatedAt || 0) - new Date(a?.date || a?.activityDate || a?.createdAt || a?.updatedAt || 0);

const toLocalDateKey = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const activityMatchesPostingWindow = (doc, { centre = '', from = '', to = '' } = {}) => {
  if (centre) {
    const docCentre = normalizeCentreName(doc?.centre || doc?.posting || doc?.location || '');
    if (docCentre !== normalizeCentreName(centre)) return false;
  }

  const dateKey = toLocalDateKey(doc?.date || doc?.activityDate || doc?.createdAt || doc?.updatedAt);
  if (from && (!dateKey || dateKey < from)) return false;
  if (to && (!dateKey || dateKey > to)) return false;
  return true;
};

const dedupeDocs = (rows) => {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const key = String(row?._id || row?.id || row?.activityId || '') || JSON.stringify([
      row?.templateSlug,
      row?.templateTitle,
      row?.date,
      row?.createdAt,
      row?.centre,
      row?.status,
    ]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Guided-history display buckets. Backend status aliases stay unchanged; this is
// only a user-facing grouping so interns/reviewers can switch through the four
// workflow states consistently.
const GUIDED_HISTORY_FILTERS = [
  { key: 'all', label: 'All', color: 'primary' },
  { key: 'draft', label: 'Draft', color: 'default' },
  { key: 'submitted', label: 'Submitted', color: 'warning' },
  { key: 'returned', label: 'Returned', color: 'error' },
  { key: 'approved', label: 'Approved', color: 'success' },
];

const guidedStatusBucket = (value) => {
  const st = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');

  if (!st || st === 'draft' || st === 'saved') return 'draft';
  if (st.includes('approved')) return 'approved';
  if (st.includes('returned') || st.includes('repeat')) return 'returned';
  if (st.includes('submitted') || st.includes('pending') || st.includes('awaiting')) return 'submitted';
  return 'other';
};

// coerce any row to an object we can work with (handles strings & primitives)
const coerceRow = (row) => {
  if (typeof row === 'string') return { description: row };
  if (!row || typeof row !== 'object') return { description: String(row ?? '') };
  return row;
};

// -------- structured / guided JSON helpers (for NVD etc.) --------
const STRUCTURED_MARKER_KEY = '__structured__';

/**
 * Accepts:
 *  - plain string (returned as-is)
 *  - JSON like {"__structured__": { "__notes": "...", ...extraFields }}
 * Returns { plain: string, extra: object|null }
 */
const parseStructuredValue = (raw) => {
  if (!raw) return { plain: '', extra: null };

  if (typeof raw !== 'string') {
    try {
      const asString = JSON.stringify(raw);
      return { plain: asString, extra: null };
    } catch {
      return { plain: String(raw), extra: null };
    }
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed[STRUCTURED_MARKER_KEY]) {
      const body = parsed[STRUCTURED_MARKER_KEY];
      const { __notes, ...rest } = body || {};
      return {
        plain: __notes || '',
        extra: Object.keys(rest || {}).length ? rest : null,
      };
    }
  } catch {
    // not JSON, fall through
  }

  return { plain: raw, extra: null };
};

// 🔍 Normalize detailed entries for accordion (handles multiple backend shapes)
const normalizeEntries = (doc) => {
  if (!doc) return [];

  // Common array shapes from backend
  if (Array.isArray(doc.items)) return doc.items;
  if (Array.isArray(doc.entries)) return doc.entries;
  if (Array.isArray(doc.logs)) return doc.logs;
  if (Array.isArray(doc.timeline)) return doc.timeline;
  if (Array.isArray(doc.details)) return doc.details;

  // Guided-activity / NVD style payloads
  if (Array.isArray(doc.steps)) return doc.steps;
  if (Array.isArray(doc.formResponses)) return doc.formResponses;
  if (Array.isArray(doc.sections)) return doc.sections;
  if (Array.isArray(doc.answers)) return doc.answers;

  // Some APIs keep everything under a generic "data" field
  if (Array.isArray(doc.data)) return doc.data;

  // If no row arrays, fall back to a single pseudo-row built from notes/summary
  const fallbackParts = [];

  if (doc.summary) {
    const { plain, extra } = parseStructuredValue(doc.summary);
    if (plain) fallbackParts.push(plain);
    else if (extra) {
      fallbackParts.push(
        Object.entries(extra)
          .map(([k, v]) => `${k}: ${String(v)}`)
          .join('; '),
      );
    }
  }

  if (doc.description && !fallbackParts.length) {
    const { plain, extra } = parseStructuredValue(doc.description);
    if (plain) fallbackParts.push(plain);
    else if (extra) {
      fallbackParts.push(
        Object.entries(extra)
          .map(([k, v]) => `${k}: ${String(v)}`)
          .join('; '),
      );
    }
  }

  const notesSource = doc.internNotes || doc.notes || doc.observerNotes;
  let notesText = '';

  if (typeof notesSource === 'string') {
    const { plain, extra } = parseStructuredValue(notesSource);
    notesText = plain;
    if (!notesText && extra) {
      notesText = Object.entries(extra)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join('; ');
    }
  } else if (notesSource && typeof notesSource === 'object') {
    notesText = Object.entries(notesSource)
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join('; ');
  }

  if (!fallbackParts.length && notesText) fallbackParts.push(notesText);

  if (!fallbackParts.length) return [];

  return [
    {
      time: '',
      description: fallbackParts.join(' — '),
      location: doc.centre || doc.posting || '',
      notes: notesText || undefined,
    },
  ];
};

export default function ActivityLog() {
  const navigate = useNavigate();

  const goApprovalWorkbench = () => navigate(tenantUrl('/dashboard/approval'));
  const goActivityLauncher = () => navigate(tenantUrl('/intern/activity/new'));
  const goComprehensiveServices = () => navigate(tenantUrl('/dashboard/operations'));

  // Reviewer mode: allow ?userId=... OR ?review=1
  const [targetUserId] = useState(() => getParam('userId') || '');
  const isReviewerView = !!(targetUserId || getParam('review'));

  // Role detection (so we can treat intern view differently in UI)
  const [userRole] = useState(() => {
    const u = jget('loggedInUser') || {};
    return u?.role || u?.userType || u?.user?.role || 'User';
  });
  const roleLower = String(userRole || '').toLowerCase();
  const isInternRole = roleLower.includes('intern');

  // Posting window + centre (affects auto-filled list AND server-side stats/visits)
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [centre, setCentre] = useState('');

  // Whether those fields were auto-filled from roster (for a small hint chip)
  const [prefilled, setPrefilled] = useState(false);

  // Quick backfill editor (manual entry for missed days)
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);

  // Fortnightly matrix (now driven from approved activities, read-only in UI)
  const [fortnightly, setFortnightly] = useState(() => createEmptyFortnightly());

  // Persisted rotation checklist block from backend. This is merged with locally
  // derived approved activity counts using max-values to avoid duplicate counts.
  const [rotationChecklist, setRotationChecklist] = useState(null);
  const [rotationChecklistLoading, setRotationChecklistLoading] = useState(false);
  const [rotationChecklistError, setRotationChecklistError] = useState('');

  // Saved/approved activities auto-filled from Start Activity (internActivities)
  const [list, setList] = useState([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  // ✅ Guided activity instances (NVD etc.) from /api/activities/*
  const [guidedList, setGuidedList] = useState([]);
  const [guidedLoading, setGuidedLoading] = useState(false);
  const [guidedError, setGuidedError] = useState('');
  const [guidedStatusFilter, setGuidedStatusFilter] = useState('all');

  // server-side stats + visits
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // ====== Try prefill period/centre from roster (mine or userId) ======
  useEffect(() => {
    const needFrom = !periodFrom;
    const needTo = !periodTo;
    const needCentre = !centre;
    if (!needFrom && !needTo && !needCentre) return;

    (async () => {
      try {
        const qs = new URLSearchParams(
          cleanParams({
            ...(isReviewerView ? { userId: targetUserId } : { mine: '1' }),
            page: '1',
            limit: '200',
          }),
        ).toString();

        const data = await fetchJson(`/api/rosters?${qs}`);
        const rows = pickArray(data);
        if (!rows.length) return;

        const activeRows = rows.filter((r) => r?.isOnDuty !== false && r?.isOnLeave !== true);
        const datedRows = activeRows
          .map((r) => ({ row: r, date: r?.date ? new Date(r.date) : null }))
          .filter(({ date }) => date && !Number.isNaN(date.getTime()))
          .sort((a, b) => a.date - b.date);
        const dates = datedRows.map(({ date }) => date);

        // For the intern's own log, use the latest roster day only as an anchor,
        // then ask the backend for the authoritative contiguous roster block that
        // contains that day. This avoids truncating an older posting at the roster
        // API's default lookback boundary (for example, 23-Feb instead of 15-Feb).
        if (!isReviewerView && datedRows.length) {
          const latest = datedRows[datedRows.length - 1];
          const anchorCentre = normalizeCentreName(
            latest?.row?.centre || latest?.row?.center || latest?.row?.location || ''
          );
          const anchorDate = latest.date.toISOString().slice(0, 10);

          try {
            const blockQs = new URLSearchParams(cleanParams({
              centre: anchorCentre || undefined,
              date: anchorDate,
            })).toString();
            const blockData = await fetchJson(
              `/api/intern-rotation-checklists/my/current${blockQs ? `?${blockQs}` : ''}`
            );
            const block = blockData?.postingBlock || null;
            const blockFrom = block?.fromDate ? new Date(block.fromDate) : null;
            const blockTo = block?.toDate ? new Date(block.toDate) : null;
            const blockCentre = normalizeCentreName(block?.centre || anchorCentre);

            if (
              blockFrom &&
              !Number.isNaN(blockFrom.getTime()) &&
              blockTo &&
              !Number.isNaN(blockTo.getTime())
            ) {
              if (needFrom) setPeriodFrom(blockFrom.toISOString().slice(0, 10));
              if (needTo) setPeriodTo(blockTo.toISOString().slice(0, 10));
              if (needCentre && blockCentre) setCentre(blockCentre);
              setPrefilled(true);
              return;
            }
          } catch {
            // Preserve the previous roster-only fallback if the block resolver is
            // unavailable in an older backend build.
          }
        }

        const centres = activeRows.map((r) => r?.centre).filter(Boolean);
        const modeCentre = (() => {
          const c = new Map();
          centres.forEach((x) => c.set(x, (c.get(x) || 0) + 1));
          let best = '';
          let bestN = 0;
          for (const [k, v] of c.entries()) {
            if (v > bestN) {
              best = k;
              bestN = v;
            }
          }
          return best || '';
        })();

        if (dates.length || modeCentre) {
          if (needFrom && dates.length) setPeriodFrom(dates[0].toISOString().slice(0, 10));
          if (needTo && dates.length) setPeriodTo(dates[dates.length - 1].toISOString().slice(0, 10));
          if (needCentre && modeCentre) setCentre(modeCentre);
          setPrefilled(true);
        }
      } catch {
        // quietly ignore roster prefill errors (no banner)
      }
    })();
    // eslint-disable-next-line
  }, [targetUserId]);

  // ====== LOAD APPROVED ACTIVITIES (internActivities) ======
  async function loadApproved() {
    try {
      setError('');
      const params = cleanParams({
        from: periodFrom || undefined,
        to: periodTo || undefined,
        status: 'approved',
        ...(isReviewerView ? { userId: targetUserId } : {}),
      });

      let arr = [];

      // 1) reviewer-specific method if present
      if (internApi.listActivitiesForUser) {
        const r1 = await internApi.listActivitiesForUser(params);
        arr = pickArray(r1);
      }

      // 2) many backends accept userId on "mine"
      if (!arr.length && internApi.listActivitiesMine) {
        const r2 = await internApi.listActivitiesMine(params);
        arr = pickArray(r2);
      }

      // 3) raw fallback (tenant-aware)
      if (!arr.length) {
        const qs = new URLSearchParams(params).toString();
        const j = await fetchJson(`/api/internActivities?${qs}`);
        arr = pickArray(j);
      }

      // ✅ Client-side status filter (safer): include approved + observed-approved
      arr = dedupeDocs((arr || []).filter((a) => {
        const st = String(a?.status || '').toLowerCase();
        return st === 'approved' || st === 'observed-approved';
      }))
        .filter((a) => {
          if (!centre) return true;
          return normalizeCentreName(a?.centre || a?.posting || a?.location) === normalizeCentreName(centre);
        })
        .sort(sortByNewest);

      setList(arr);
    } catch (e) {
      const status = e?.status || 0;
      // Quiet 400/404 -> treat as "no data yet"
      if (status === 400 || status === 404) {
        setList([]);
        setError('');
      } else {
        setError(e?.message || 'Failed to load activities');
        setList([]);
      }
    }
  }

  // ✅ LOAD GUIDED ACTIVITY INSTANCES (NVD etc.) ======
  async function loadGuided() {
    try {
      setGuidedLoading(true);
      setGuidedError('');

      const common = cleanParams({
        // Reviewer history follows the selected filter window. An intern's own
        // guided history intentionally spans all roster postings so older linked
        // activities remain reachable even when the current posting card points
        // at a different centre/date block.
        ...(isReviewerView
          ? {
              from: periodFrom || undefined,
              to: periodTo || undefined,
              centre: centre || undefined,
            }
          : {}),
        // show everything (draft/submitted/returned/approved/repeat + observed variants)
        status:
          'draft,saved,submitted,returned,returned-to-edit,approved,repeat,repeat-required,observed-submitted,observed-approved,observed-returned,observed-repeat,pending,awaiting_review,awaiting-review,awaiting review,awaiting-observer',
        page: 1,
        limit: 500,
      });

      let arr = [];

      if (isReviewerView) {
        const r = await activityApi.reviewerQueue(common);
        arr = pickArray(r);
        if (targetUserId) {
          arr = arr.filter((d) => String(d?.internUserId || '') === String(targetUserId));
        }
      } else {
        const r = await activityApi.listMine(common);
        arr = pickArray(r);
      }

      const nextGuided = dedupeDocs(arr || [])
        .filter((doc) => {
          if (!isReviewerView || !centre) return true;
          return normalizeCentreName(doc?.centre || doc?.posting || doc?.location) === normalizeCentreName(centre);
        })
        .sort(sortByNewest);

      setGuidedList(nextGuided);
    } catch (e) {
      const status = e?.status || 0;
      if (status === 400 || status === 404) {
        setGuidedList([]);
        setGuidedError('');
      } else {
        setGuidedList([]);
        setGuidedError(e?.message || 'Failed to load guided activities');
      }
    } finally {
      setGuidedLoading(false);
    }
  }

  // ====== LOAD SERVER-SIDE COUNTS (per intern) ======
  async function loadStats() {
    try {
      setStatsLoading(true);
      const baseParams = cleanParams({
        from: periodFrom || undefined,
        to: periodTo || undefined,
        ...(isReviewerView ? { userId: targetUserId } : { mine: '1' }),
      });

      let out = null;

      if (internApi.getStats) out = await internApi.getStats(baseParams);
      if (!out) {
        const qs = new URLSearchParams(baseParams).toString();
        out = await fetchJson(`/api/internActivities/stats?${qs}`);
      }

      if (out && (out.success || out.totals || out.byCentre || out.visits)) {
        setStats(out);
      } else {
        setStats(null);
      }
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }

  // ====== LOAD PERSISTED ROTATION CHECKLIST BLOCK ======
  async function loadRotationChecklist() {
    if (isReviewerView) {
      setRotationChecklist(null);
      setRotationChecklistError('');
      return;
    }

    try {
      setRotationChecklistLoading(true);
      setRotationChecklistError('');

      const today = new Date().toISOString().slice(0, 10);
      const asOf = periodFrom || today;
      const qs = new URLSearchParams(cleanParams({
        centre: centre || undefined,
        date: asOf,
      })).toString();

      const data = await fetchJson(`/api/intern-rotation-checklists/my/current${qs ? `?${qs}` : ''}`);
      const checklist = data?.checklist || data?.item || data?.data || null;
      setRotationChecklist(checklist || null);
    } catch (e) {
      const status = e?.status || 0;
      if (status === 400 || status === 404) {
        setRotationChecklist(null);
        setRotationChecklistError('');
      } else {
        setRotationChecklist(null);
        setRotationChecklistError(e?.message || 'Rotation checklist could not be loaded');
      }
    } finally {
      setRotationChecklistLoading(false);
    }
  }

  useEffect(() => {
    loadApproved();
    loadGuided();
    loadStats();
    loadRotationChecklist();
    // eslint-disable-next-line
  }, [periodFrom, periodTo, targetUserId, centre]);

  // ====== derive rotation report counts from approved activity records ======
  useEffect(() => {
    const next = createEmptyFortnightly();

    // Guided activities and OPD visits remain separate flows.
    // This report only auto-counts approved guided/backfilled activity records that match
    // the rotation-report catalogue. Draft/submitted/awaiting records are intentionally not counted.
    const seen = new Set();
    const sources = [];

    (list || []).forEach((d) => {
      const key = String(d?._id || d?.id || d?.activityId || '');
      if (!key || seen.has(`approved:${key}`)) return;
      seen.add(`approved:${key}`);
      sources.push({ doc: d, forceApproved: true });
    });

    (guidedList || [])
      .filter((d) => activityMatchesPostingWindow(d, {
        centre,
        from: periodFrom,
        to: periodTo,
      }))
      .forEach((d) => {
        const key = String(d?._id || d?.id || d?.activityId || '');
        if (!key || seen.has(`guided:${key}`)) return;
        seen.add(`guided:${key}`);
        sources.push({ doc: d, forceApproved: false });
      });

    sources.forEach(({ doc, forceApproved }) => {
      if (!isApprovedForRotationReport(doc, forceApproved)) return;
      const target = resolveFortnightlyTarget(doc);
      if (!target) return;
      addFortnightlyCount(next, target, doc);
    });

    // Merge server-side rotation checklist counts without adding duplicates.
    // Backend-synced/manual cells are treated as authoritative minimum values.
    mergeStoredChecklistIntoFortnightly(next, rotationChecklist);

    setFortnightly(next);
  }, [list, guidedList, rotationChecklist, centre, periodFrom, periodTo]);


// ====== SEARCH over auto-filled list (unchanged but more robust rows) ======
  const filtered = useMemo(() => {
    const baseList = (list || []).slice().sort(sortByNewest);
    if (!query.trim()) return baseList;
    const q = query.toLowerCase();
    return baseList.filter((doc) => {
      const base = `${doc?.centre ?? ''} ${doc?.posting ?? ''} ${
        doc?.status ?? ''
      } ${doc?.internNotes ?? ''} ${doc?.templateTitle ?? ''} ${
        doc?.templateSlug ?? ''
      } ${doc?.activityKey ?? ''} ${doc?.corePracticeAreaTitle ?? ''} ${doc?.practiceAreaTitle ?? ''} ${doc?.finalGroup ?? ''}`.toLowerCase();
      const lines = normalizeEntries(doc).map((row) => {
        const r = coerceRow(row);
        return `${r?.date ?? ''} ${r?.time ?? ''} ${r?.description ?? ''} ${
          r?.location ?? ''
        } ${r?.notes ?? ''}`.toLowerCase();
      });
      return base.includes(q) || lines.some((s) => s.includes(q));
    });
  }, [list, query]);

  // ✅ Search for guided activities using same query box (no extra UI)
  const guidedFiltered = useMemo(() => {
    const baseList = (guidedList || []).slice().sort(sortByNewest);
    if (!query.trim()) return baseList;
    const q = query.toLowerCase();
    return baseList.filter((doc) => {
      const base = `${doc?.centre ?? ''} ${doc?.shift ?? ''} ${doc?.templateTitle ?? ''} ${doc?.templateSlug ?? ''} ${doc?.activityKey ?? ''} ${doc?.corePracticeAreaTitle ?? ''} ${doc?.practiceAreaTitle ?? ''} ${doc?.finalGroup ?? ''} ${doc?.status ?? ''}`.toLowerCase();
      const details = normalizeEntries(doc)
        .map((row) => {
          const r = coerceRow(row);
          return `${r?.description ?? ''} ${r?.notes ?? ''}`.toLowerCase();
        })
        .join(' ');
      return base.includes(q) || details.includes(q);
    });
  }, [guidedList, query]);

  const guidedStatusSummary = useMemo(() => {
    const base = { total: 0, draft: 0, submitted: 0, returned: 0, approved: 0, other: 0 };
    (guidedFiltered || []).forEach((doc) => {
      base.total += 1;
      const bucket = guidedStatusBucket(doc?.status);
      if (Object.prototype.hasOwnProperty.call(base, bucket)) base[bucket] += 1;
      else base.other += 1;
    });
    return base;
  }, [guidedFiltered]);

  const guidedVisible = useMemo(() => {
    if (guidedStatusFilter === 'all') return guidedFiltered;
    return (guidedFiltered || []).filter((doc) => guidedStatusBucket(doc?.status) === guidedStatusFilter);
  }, [guidedFiltered, guidedStatusFilter]);

  const activeGuidedStatusLabel =
    GUIDED_HISTORY_FILTERS.find((item) => item.key === guidedStatusFilter)?.label || 'All';

  const combinedHistoryCount = useMemo(() => {
    const keys = new Set();
    [...(filtered || []), ...(guidedFiltered || [])].forEach((doc) => {
      keys.add(String(doc?._id || doc?.id || doc?.activityId || `${doc?.templateSlug || ''}-${doc?.date || doc?.createdAt || ''}-${doc?.centre || ''}`));
    });
    return keys.size;
  }, [filtered, guidedFiltered]);

  // ====== BACKFILL (manual) ======
  const addRow = () => setItems((a) => [...a, emptyItem()]);
  const rmRow = (i) => setItems((a) => a.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) =>
    setItems((a) => a.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const addFortnightlyToBackfill = () => {
    const out = [];
    Object.entries(fortnightly).forEach(([sec, rows]) => {
      Object.entries(rows).forEach(([title, cell]) => {
        const obs = Number(cell?.observed || 0);
        const perf = Number(cell?.performed || 0);
        const rem = String(cell?.remark || '').trim();
        if (obs > 0 || perf > 0 || rem) {
          out.push({
            date: '',
            time: '',
            description: `Fortnightly · ${sec} · ${title}`,
            location: centre || '',
            patientsSeen: 0,
            notes: `Observed: ${obs} | Performed: ${perf}${rem ? ` | Remark: ${rem}` : ''}`,
          });
        }
      });
    });
    if (!out.length) return;
    setItems((a) => [...a, ...out.map((e) => ({ ...e, date: '' }))]);
  };

  const backfillValid = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return items.some((r) => {
      const d = String(r.date || '').slice(0, 10);
      const descOk = String(r.description || '').trim().length >= 3;
      return d && d < today && descOk;
    });
  }, [items]);
  async function saveBackfill() {
    try {
      setSaving(true);
      setError('');
      const byDate = {};
      for (const r of items) {
        const d = (r.date || '').slice(0, 10);
        if (!d) continue;
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(r);
      }
      const dates = Object.keys(byDate);
      for (const d of dates) {
        const payload = {
          date: d,
          centre: centre || undefined,
          posting: undefined,
          notes: undefined,
          entries: byDate[d].map((r) => ({
            time: r.time || undefined,
            description: r.description || '',
            hours: r.hours ? Number(r.hours) : undefined,
            category: r.category || undefined,
            location: r.location || undefined,
            notes: r.notes || undefined,
          })),
          ...(isReviewerView ? { userId: targetUserId } : {}),
        };
        await internApi.saveDailyLog(payload);
      }
      setItems([emptyItem()]);
      await loadApproved();
      await loadStats();
      await loadRotationChecklist();
    } catch (e) {
      setError(e?.message || 'Backfill save failed');
    } finally {
      setSaving(false);
    }
  }

  async function submit(id) {
    try {
      await internApi.submitActivity(id);
      await loadApproved();
      await loadStats();
      await loadRotationChecklist();
    } catch (e) {
      setError(e?.message || 'Submit failed');
    }
  }

  async function generatePdf(id) {
    try {
      const out = await internApi.generateActivityPdf?.(id);
      const pdfUrl = out?.pdfUrl || tenantUrl(`/api/internActivities/${encodeURIComponent(id)}/pdf`);
      window.open(pdfUrl, '_blank', 'noopener');
    } catch (e) {
      setError(e?.message || 'PDF generation failed');
    }
  }

  // ✅ simple viewer routes for guided instances
  const openGuided = (doc) => {
    const st = String(doc?.status || '').toLowerCase();
    const canContinue = !isReviewerView && (st === 'draft' || st === 'returned');

    const from = `${window.location.pathname}${window.location.search}`;

    if (canContinue) {
      navigate(tenantUrl(`/intern/activity/guided/${doc._id}`), {
        state: { from },
      });
      return;
    }

    // Interns may open older activities, but must remain view-only (decision disabled in ObserverSignoff)
    navigate(tenantUrl(`/intern/activity/observe/${doc._id}`), {
      state: { from, viewOnly: !isReviewerView && isInternRole },
    });
  };

  // ====== small helpers for stats UI ======
  const totalActivities = stats?.totals?.activities ?? null;
  const byCentre = Array.isArray(stats?.byCentre) ? stats.byCentre : [];

  const postingHeaderSet = !!(periodFrom || periodTo || centre);

  return (
    <Box className="space-y-4">
      {/* Header */}
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {isReviewerView
              ? 'Activity Review & History'
              : isInternRole
                ? 'Activity History'
                : 'Activity Work History'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            This page is for activity history, reporting, and reopening existing work. Use Start Activity to launch new guided activities and Comprehensive Services for citizen and OPD workflow.
          </Typography>
        </Box>
        <Box className="flex items-center gap-2 flex-wrap">
          {!isReviewerView && (
            <Button size="small" variant="outlined" onClick={goActivityLauncher}>
              Start Activity
            </Button>
          )}
          {!isReviewerView && (
            <Button size="small" variant="outlined" onClick={goComprehensiveServices}>
              Comprehensive Services
            </Button>
          )}
          {isReviewerView && (
            <Button size="small" variant="outlined" onClick={goApprovalWorkbench}>
              Reviewer Workbench
            </Button>
          )}
          {!isReviewerView && (
            <Button size="small" variant="outlined" onClick={() => navigate(tenantUrl('/intern/activity/returned'))}>
              Returned guided activities
            </Button>
          )}
          <TextField
            size="small"
            placeholder="Search in activities…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Filters / Posting Window */}
      {isReviewerView ? (
        <>
          {/* Reviewer: keep full filter bar */}
          <Paper variant="outlined" className="p-3">
            <Box className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <TextField
                label="Period From"
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Period To"
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField label="Centre" select value={centre} onChange={(e) => setCentre(e.target.value)}>
                <MenuItem value=""></MenuItem>
                {CENTRES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Paper>

          {/* Posting Window header (summary) */}
          {postingHeaderSet && (
            <Paper variant="outlined" className="p-3">
              <Box className="flex flex-wrap items-center gap-2">
                <Typography variant="subtitle2" fontWeight={700}>
                  Posting Window
                </Typography>
                {centre && <Chip size="small" label={normalizeCentreName(centre)} />}
                {periodFrom && <Chip size="small" variant="outlined" label={`From: ${periodFrom}`} />}
                {periodTo && <Chip size="small" variant="outlined" label={`To: ${periodTo}`} />}
                {prefilled && (
                  <Chip
                    size="small"
                    color="info"
                    icon={<InfoOutlinedIcon fontSize="small" />}
                    label="Prefilled from Roster"
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>
          )}
        </>
      ) : (
        // Intern own log: single Posting Window card only, with non-editable centre and editable dates
        postingHeaderSet && (
          <Paper variant="outlined" className="p-3">
            <Box className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <Typography variant="subtitle2" fontWeight={700}>
                Posting Window (current posting)
              </Typography>
              {prefilled && (
                <Chip
                  size="small"
                  color="info"
                  icon={<InfoOutlinedIcon fontSize="small" />}
                  label="Prefilled from Roster"
                  variant="outlined"
                />
              )}
            </Box>
            <Box className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TextField label="Centre" value={normalizeCentreName(centre) || 'Not set'} InputProps={{ readOnly: true }} />
              <TextField
                label="From"
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Paper>
        )
      )}

      <Box className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" sx={{ opacity: 0.72 }}>
            Approved activity records
          </Typography>
          <Typography variant="h6">{filtered.length}</Typography>
        </Paper>
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" sx={{ opacity: 0.72 }}>
            Guided activity records
          </Typography>
          <Typography variant="h6">{guidedFiltered.length}</Typography>
        </Paper>
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" sx={{ opacity: 0.72 }}>
            Combined history rows
          </Typography>
          <Typography variant="h6">{combinedHistoryCount}</Typography>
        </Paper>
        <Paper variant="outlined" className="p-3">
          <Typography variant="caption" sx={{ opacity: 0.72 }}>
            Guided approvals awaiting action
          </Typography>
          <Typography variant="h6">{guidedStatusSummary.submitted}</Typography>
        </Paper>
      </Box>

      {/* Server-side activity summary */}
      <Paper variant="outlined" className="p-3">
        <Typography variant="subtitle2" fontWeight={700} className="mb-2">
          Activity Summary (server-side)
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }} className="mb-2">
          Summary and counts are shown here for reporting. Use this page to review what already exists rather than as a separate launch hub.
        </Typography>
        {statsLoading ? (
          <div className="flex items-center gap-2 opacity-80">
            <CircularProgress size={18} />
            <span>Loading…</span>
          </div>
        ) : (
          <>
            <Box className="flex flex-wrap gap-6">
              <div>
                <div className="text-sm opacity-70">Approved Activities</div>
                <div className="text-2xl font-bold">{totalActivities ?? '—'}</div>
              </div>
            </Box>

            {byCentre.length > 0 && (
              <>
                <Divider className="my-2" />
                <div className="text-sm font-semibold mb-1">By Centre</div>
                <Box className="flex flex-wrap gap-1">
                  {byCentre.map((row, i) => (
                    <Chip key={`a-${i}`} size="small" label={`${normalizeCentreName(row.centre) || '—'}: ${row.activities}`} />
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </Paper>

      {/* ✅ Guided Activities panel */}
      <Paper variant="outlined" className="p-3 space-y-2">
        <Box className="flex flex-wrap items-center justify-between gap-2">
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Guided Activity History (NVD etc.)
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              {isReviewerView
                ? 'Guided history follows the selected reviewer filters.'
                : 'Guided history is shown across roster postings; the rotation report below remains scoped to the current posting window.'}
            </Typography>
          </Box>
          <Box className="flex items-center gap-2 flex-wrap">
            {GUIDED_HISTORY_FILTERS.map((item) => {
              const count = item.key === 'all' ? guidedStatusSummary.total : guidedStatusSummary[item.key] || 0;
              const selected = guidedStatusFilter === item.key;
              return (
                <Chip
                  key={item.key}
                  size="small"
                  clickable
                  label={`${item.label}: ${count}`}
                  color={item.color}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => setGuidedStatusFilter(item.key)}
                  aria-pressed={selected}
                  sx={{ fontWeight: selected ? 700 : 400 }}
                />
              );
            })}
            <Button size="small" variant="outlined" onClick={loadGuided} disabled={guidedLoading}>
              {guidedLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {guidedError && <Alert severity="error">{guidedError}</Alert>}

        {guidedLoading ? (
          <div className="flex items-center gap-2 opacity-80">
            <CircularProgress size={18} />
            <span>Loading…</span>
          </div>
        ) : guidedVisible.length === 0 ? (
          <Alert severity="info">
            {guidedStatusFilter !== 'all'
              ? `No ${activeGuidedStatusLabel.toLowerCase()} guided activities found with the current search/filter.`
              : isReviewerView
                ? 'No guided activities found with the current reviewer filters.'
                : 'No guided activities found in your activity history. If an activity was performed, confirm it was saved/submitted from the Guided Run screen.'}
          </Alert>
        ) : (
          <Box className="space-y-2">
            {guidedVisible.map((doc) => {
              const d = doc.date || doc.createdAt || doc.updatedAt;
              const when = d ? new Date(d).toDateString() : '—';
              const title =
                doc.templateTitle ||
                doc.template?.title ||
                doc.templateName ||
                doc.title ||
                doc.templateSlug ||
                'Guided Activity';

              const st = String(doc.status || '').toUpperCase();

              const internLabel = isReviewerView ? doc.internName || doc.userName || doc.internEmail || '' : '';

              return (
                <Paper key={doc._id} variant="outlined" className="p-2">
                  <Box className="flex flex-wrap items-center justify-between gap-2">
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {sanitizeDisplayText(title)}
                      </Typography>
                      <Typography variant="caption" className="opacity-80">
                        {when} • {normalizeCentreName(doc.centre || centre) || '—'}
                        {internLabel ? ` • Intern: ${internLabel}` : ''}
                      </Typography>
                    </Box>

                    <Box className="flex items-center gap-1 flex-wrap justify-end">
                      <Chip size="small" label={st} variant="outlined" />
                      <Button size="small" variant="outlined" onClick={() => openGuided(doc)}>
                        Open
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Fortnightly checklist – read-only, auto-filled
          NOTE: Hide this completely in reviewer (non-intern) view */}
      {!isReviewerView && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Rotation Report (Core Practice Areas → Competencies → Skills) — Observed / Performed / Remark</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="flex flex-wrap items-center gap-2 mb-3">
              <Chip
                size="small"
                variant="outlined"
                label={rotationChecklist ? 'Server checklist merged' : 'Derived from approved activities'}
              />
              {rotationChecklistLoading && <Chip size="small" variant="outlined" label="Loading server checklist…" />}
              {rotationChecklistError && <Chip size="small" color="warning" variant="outlined" label="Checklist sync unavailable" />}
            </Box>
            {rotationChecklistError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {rotationChecklistError}. Showing locally derived approved activity counts.
              </Alert>
            )}
            <Box className="space-y-6">
              {FORTNIGHTLY_SECTIONS.map((sec) => (
                <Paper key={sec.title} variant="outlined" className="p-3 space-y-2">
                  <Typography variant="subtitle1" fontWeight={700} className="mb-1">
                    {sec.title}
                  </Typography>
                  {FORTNIGHTLY_SECTION_NOTES[sec.title] && (
                    <Typography variant="caption" sx={{ opacity: 0.72 }} className="block mb-2">
                      {FORTNIGHTLY_SECTION_NOTES[sec.title]}
                    </Typography>
                  )}
                  <Box className="hidden md:grid md:grid-cols-12 text-xs font-semibold opacity-70 mb-1">
                    <Box className="md:col-span-6">Activity</Box>
                    <Box className="md:col-span-2 text-center">Observed</Box>
                    <Box className="md:col-span-2 text-center">Performed</Box>
                    <Box className="md:col-span-2 text-center">Remark</Box>
                  </Box>
                  {sec.items.map((it) => {
                    const cell = fortnightly[sec.title]?.[it] || wk();
                    return (
                      <Box key={it} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start py-1">
                        <Typography variant="body2" className="md:col-span-6">
                          {it}
                        </Typography>
                        <TextField className="md:col-span-2" size="small" value={cell.observed} InputProps={{ readOnly: true }} />
                        <TextField className="md:col-span-2" size="small" value={cell.performed} InputProps={{ readOnly: true }} />
                        <TextField className="md:col-span-2" size="small" value={cell.remark} InputProps={{ readOnly: true }} />
                      </Box>
                    );
                  })}
                  <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addFortnightlyToBackfill}>
                    Add Fortnightly items to Backfill editor
                  </Button>
                </Paper>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Auto-filled timeline (approved) */}
      <Paper variant="outlined" className="p-3 space-y-2">
        <Typography variant="subtitle2" fontWeight={700}>
          Approved Activity Timeline
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          This timeline is kept as a history/reporting lane. New work should start from Start Activity, while guided activities remain in the separate guided history panel above.
        </Typography>
        <Divider className="my-2" />
        {filtered.length === 0 && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No approved activities in this window.
          </Typography>
        )}
        {filtered.map((doc) => {
          const dateSource = doc.date || doc.activityDate || doc.createdAt || doc.updatedAt;
          const dateLabel = dateSource ? new Date(dateSource).toDateString() : '—';

          const internLabel = isReviewerView
            ? (
                doc.internName ||
                doc.userName ||
                (doc.intern && (doc.intern.name || doc.intern.fullName)) ||
                (doc.user && (doc.user.name || doc.user.fullName)) ||
                doc.userEmail ||
                (doc.user && doc.user.email) ||
                ''
              )
            : '';

          const templateLabel =
            doc.templateTitle ||
            (doc.template && (doc.template.title || doc.template.name)) ||
            doc.activityTitle ||
            doc.title ||
            doc.templateSlug ||
            'Activity';

          const entryList = normalizeEntries(doc);

          return (
            <Accordion key={doc._id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box className="flex flex-col md:flex-row md:items-center md:justify-between w-full gap-1">
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    <strong>{sanitizeDisplayText(templateLabel)}</strong>
                    {' • '}
                    {dateLabel}
                    {internLabel && ` • Intern: ${internLabel}`}
                    {' • '}
                    {normalizeCentreName(doc.centre) || '—'}
                    {' • '}
                    {doc.posting || '—'}
                  </Typography>
                  <Chip size="small" label={String(doc.status || '').toUpperCase()} variant="outlined" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {entryList.length === 0 ? (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    No detailed entries available for this activity.
                  </Typography>
                ) : (
                  entryList.map((row, idx) => {
                    const r = coerceRow(row);

                    const rawDesc = r.description || r.text || r.label || r.question || r.title || '';

                    let rawNotes = '';
                    if (typeof r.notes === 'string') rawNotes = r.notes;
                    else if (typeof r.valueText === 'string') rawNotes = r.valueText;

                    const { plain: descPlain, extra: descExtra } = parseStructuredValue(rawDesc);
                    const { plain: notesPlain, extra: notesExtra } = parseStructuredValue(
                      rawNotes && rawNotes !== rawDesc ? rawNotes : '',
                    );

                    const desc = descPlain || rawDesc || '(no description)';

                    let notesText = notesPlain || '';
                    if (!notesText) {
                      if (r.notes && typeof r.notes === 'object') {
                        notesText = Object.entries(r.notes)
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join('; ');
                      } else if (r.answer !== undefined) {
                        notesText = String(r.answer);
                      } else if (r.value !== undefined) {
                        notesText = String(r.value);
                      } else if (descExtra) {
                        notesText = Object.entries(descExtra)
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join('; ');
                      } else if (notesExtra) {
                        notesText = Object.entries(notesExtra)
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join('; ');
                      }
                    }

                    return (
                      <Box key={idx} className="text-sm mb-1">
                        <strong>{r.time ? `${r.time}: ` : ''}</strong>
                        {desc}
                        {r.location ? ` — ${r.location}` : ''}
                        {notesText ? ` — ${notesText}` : ''}
                      </Box>
                    );
                  })
                )}
                <Box className="flex gap-1 mt-2">
                  {(doc.status === 'draft' || doc.status === 'returned') && !isReviewerView && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => submit(doc._id)}
                      startIcon={<SendIcon />}
                    >
                      Submit for Review
                    </Button>
                  )}
                  <Button size="small" variant="outlined" onClick={() => generatePdf(doc._id)} startIcon={<PictureAsPdfIcon />}>
                    Generate PDF
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Paper>

      {/* Missed-Day Additions (Backfill) */}
      {!isReviewerView && (
        <Paper variant="outlined" className="p-3 space-y-3">
          <Typography variant="subtitle2" fontWeight={700}>
            Backfill Missed Activity Entries
          </Typography>
          <Alert severity="info">
            Use this only to add activities for a <strong>past date</strong> you forgot to enter. Start new guided activities from the{' '}
            <em>Start Activity</em> page.
          </Alert>
          {items.map((it, i) => (
            <Paper
              key={i}
              variant="outlined"
              className="p-3 border dark:border-neutral-700"
              sx={{ background: 'transparent' }}
            >
              <Box className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <TextField
                  className="md:col-span-2"
                  label="Date (past)"
                  type="date"
                  value={it.date}
                  onChange={(e) => setItem(i, 'date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  className="md:col-span-2"
                  label="Time"
                  value={it.time}
                  onChange={(e) => setItem(i, 'time', e.target.value)}
                  size="small"
                />
                <TextField
                  className="md:col-span-5"
                  label="Description"
                  value={it.description}
                  onChange={(e) => setItem(i, 'description', e.target.value)}
                  size="small"
                />
                <TextField
                  className="md:col-span-2"
                  label="Location"
                  value={it.location}
                  onChange={(e) => setItem(i, 'location', e.target.value)}
                  size="small"
                />
                <Box className="md:col-span-1 flex items-center justify-end">
                  <Tooltip title={items.length === 1 ? 'At least one row is required' : 'Remove row'}>
                    <span>
                      <IconButton color="error" onClick={() => rmRow(i)} disabled={items.length === 1} size="small">
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <TextField
                  className="md:col-span-12"
                  label="Notes"
                  value={it.notes}
                  onChange={(e) => setItem(i, 'notes', e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                />
              </Box>
            </Paper>
          ))}
          <Box className="flex gap-2 pt-1">
            <Button variant="outlined" onClick={addRow} startIcon={<AddCircleOutlineIcon />}>
              Add Row
            </Button>
            <Button
              variant="contained"
              onClick={saveBackfill}
              disabled={!backfillValid || saving}
              startIcon={<SaveIcon />}
            >
              {saving ? 'Saving…' : 'Save Backfill'}
            </Button>
          </Box>
        </Paper>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </Box>
  );
}
